import { isPast } from 'date-fns';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { getEditGamePageUrl } from 'constants/pages';
import { getCancelTournamentEndpoint } from 'constants/payloads/tournaments/cancel';
import { PlayRequestStatusesEnum } from 'types/generated/client';
import { useApiGateway } from 'hooks/useApi';
import { useModal } from 'hooks/useModal';
import { useMyGameInvites } from 'hooks/useMyGameInvites';
import { useViewer } from 'hooks/useViewer';
import CheckCircle from 'svg/CheckCircle';
import CloseIcon from 'svg/CloseIcon';
import Pencil from 'svg/Pencil';
import {
  CancelRequestModal,
  YourRequestCard,
  useMyGameRequest,
} from 'screens/Find/components/RequestToPlay';
import type { FindGame } from 'screens/Find/types';
import { Button } from 'components/Button';
import DetailModalShell from 'components/Find/DetailModalShell';
import {
  MatchDescription,
  MatchLocationDetails,
  MatchOrganizer,
  MatchPrice,
  MatchSummary,
} from 'components/Find/MatchDetails';
import { isUnlimitedOpenPlay } from 'components/Find/gameFormat';
import AlertModal from 'components/modals/AlertModal';

interface Props {
  game: FindGame | null;
  isOpen: boolean;
  onClose: () => void;
  onRequestToPlay?: (gameId: string) => void;
  /** Force the request footer off — e.g. from a game's chat. Default hides it for the host. */
  hideRequestFooter?: boolean;
  /** Lets the caller refetch or leave the page the cancelled game backed. */
  onCancelled?: () => void;
}

/** Detail view for a game card on the Find "Games" tab. */
export default function MatchDetailsModal({
  game,
  isOpen,
  onClose,
  onRequestToPlay,
  hideRequestFooter,
  onCancelled,
}: Props) {
  const router = useRouter();
  const { userId } = useViewer();
  const { myRequest, refetch } = useMyGameRequest(game?.id);
  const { myRequest: mySeat } = useMyGameRequest(game?.id, PlayRequestStatusesEnum.Accepted);
  const {
    getInviteForEvent,
    acceptInvite,
    declineInvite,
    isSubmitting: isAnsweringInvite,
  } = useMyGameInvites();
  // The host of their own game has nothing to request — they get Cancel/Edit instead.
  const isOwnGame = Boolean(game?.hostUserId && userId && game.hostUserId === userId);
  // An invited player answers the invite instead of asking to join: accepting takes
  // the seat the host already set aside, so it never becomes a request.
  const myInvite = getInviteForEvent(game?.id);
  // Reached by link only (private games never list): without an invite there is no way in.
  const isLockedOut = Boolean(game?.isInviteOnly && !myInvite && !myRequest && !mySeat);
  // Requests and invite accepts are refused once a game starts, so don't offer them.
  const hasStartedForViewer = Boolean(
    game && isPast(new Date(game.startDateTime)) && !isOwnGame && !mySeat,
  );
  // One modal at a time: the sheet hides while a confirm is up.
  const cancelModal = useModal();
  const cancelGameModal = useModal();

  const { post: cancelGame, isLoading: isCancellingGame } = useApiGateway<undefined, unknown>();
  const handleCancelGame = async () => {
    if (!game) return;
    // A game is an event row, so it cancels through the same endpoint.
    const response = await cancelGame({ endpoint: getCancelTournamentEndpoint(game.id) });
    if (response.isError) {
      toast.error(response.error || 'Could not cancel this game');
      return;
    }
    cancelGameModal.closeModal();
    onClose();
    onCancelled?.();
  };

  // Figma's host footer: the two actions only the organizer has.
  const hostFooter = game && (
    <div className="flex gap-ds-md lg:gap-ds-xl">
      <Button
        variant="brand-secondary"
        size="lg"
        onClick={() => cancelGameModal.openModal()}
        className="lg:min-w-0 lg:flex-1"
      >
        Cancel game
      </Button>
      <Button
        variant="secondary"
        size="lg"
        iconLeft={<Pencil className="h-5 w-5" />}
        onClick={() => router.push(getEditGamePageUrl(game.id))}
        className="lg:min-w-0 lg:flex-1"
      >
        Edit
      </Button>
    </div>
  );

  const inviteFooter = myInvite && (
    <div className="flex gap-ds-md lg:gap-ds-xl">
      <Button
        variant="primary"
        size="lg"
        disabled={isAnsweringInvite}
        iconLeft={<CloseIcon className="h-5 w-5" />}
        onClick={async () => {
          const didDecline = await declineInvite(myInvite.id);
          if (didDecline) {
            onClose();
          }
        }}
        className="lg:min-w-0 lg:flex-1"
      >
        Decline
      </Button>
      {game?.isFull ? (
        <Button variant="brand" size="lg" disabled className="lg:min-w-0 lg:flex-1">
          This game is full
        </Button>
      ) : (
        <Button
          variant="brand"
          size="lg"
          loading={isAnsweringInvite}
          iconLeft={<CheckCircle className="h-5 w-5" />}
          onClick={() => acceptInvite(myInvite.id)}
          className="lg:min-w-0 lg:flex-1"
        >
          Accept invite
        </Button>
      )}
    </div>
  );

  return (
    <>
      <DetailModalShell
        isOpen={isOpen && !cancelModal.isOpen && !cancelGameModal.isOpen}
        onClose={onClose}
        onRequestToPlay={() => game && onRequestToPlay?.(game.id)}
        hideRequestFooter={
          hideRequestFooter ||
          isOwnGame ||
          !!myInvite ||
          !!mySeat ||
          isLockedOut ||
          hasStartedForViewer
        }
        sizeDesktop="lg"
        // A full game can't be requested, but the viewer's own live request still shows.
        hasFooterDividerAtDesktop={isOwnGame || !!myInvite}
        footer={
          isOwnGame ? (
            hostFooter
          ) : hasStartedForViewer && !hideRequestFooter ? (
            <Button variant="brand" size="lg" disabled>
              This game has already started
            </Button>
          ) : myInvite ? (
            inviteFooter
          ) : isLockedOut && !hideRequestFooter ? (
            <Button variant="brand" size="lg" disabled>
              This game is invite only
            </Button>
          ) : game?.isFull && !myRequest && !hideRequestFooter ? (
            <Button variant="brand" size="lg" disabled>
              This game is full
            </Button>
          ) : undefined
        }
        activeRequest={
          myRequest ? (
            <YourRequestCard
              request={myRequest}
              onCancel={cancelModal.openModal}
              desktopLayout="card"
              buttonSizeDesktop="md"
            />
          ) : undefined
        }
      >
        {game && (
          <>
            <MatchSummary game={game} sizeDesktop="lg" />

            {game.details && (
              <>
                <div className="my-ds-xl h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />

                <div className="flex flex-col gap-ds-3xl">
                  <MatchOrganizer organizer={game.details.organizer} />
                  <MatchDescription
                    audience={game.details.audience}
                    audienceLabel={game.details.audienceLabel}
                    description={game.details.description}
                    participantLimit={game.participantLimit}
                    isUnlimited={isUnlimitedOpenPlay(game)}
                  />
                  <MatchLocationDetails
                    venue={game.details.venue}
                    isCourtBooked={game.isCourtBooked}
                  />
                  <MatchPrice
                    courtCostPerPlayer={game.details.courtCostPerPlayer}
                    paymentMethod={game.details.paymentMethod}
                  />
                </div>
              </>
            )}
          </>
        )}
      </DetailModalShell>
      <CancelRequestModal
        requestId={myRequest?.id ?? null}
        isOpen={cancelModal.isOpen}
        onClose={cancelModal.closeModal}
        onCancelled={refetch}
      />

      <AlertModal
        isOpen={cancelGameModal.isOpen}
        handleClose={cancelGameModal.closeModal}
        title="Cancel game"
        description="Everyone in this game will be notified and this chat will close."
        confirmBtnText="Cancel game"
        confirmBtnVariant="brand"
        cancelBtnText="Go back"
        secondaryFirst
        isWideDesktop
        loading={isCancellingGame}
        onConfirm={handleCancelGame}
        onCancel={cancelGameModal.closeModal}
      />
    </>
  );
}
